import type { TFunction } from 'i18next';
import { Plus, RotateCw } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AmbulanceList from '@/domains/traffic-monitoring/components/AmbulanceList/AmbulanceList';
import BackLink from '@/domains/traffic-monitoring/components/BackLink/BackLink';
import ModalAddAmbulance from '@/domains/traffic-monitoring/components/ModalAddAmbulance/ModalAddAmbulance';
import PlaceHolderAmbulance from '@/domains/traffic-monitoring/components/PlaceHolderAmbulance/PlaceHolderAmbulance';
import TabControl from '@/domains/traffic-monitoring/components/TabControl/TabControl';
import { useAmbulanceDispatch } from '@/domains/traffic-monitoring/hooks/useAmbulanceDispatch';
import { cn } from '@/lib/utils';
import Header from '@/shared/components/layout/Header/Header';
import Button from '@/shared/components/ui/Button';
import { showToast } from '@/shared/utils/showToast';

const SEARCH_DEBOUNCE_MS = 300;
// Matches the 1-5 range every number_of_victims dropdown offers elsewhere
// (ReportRegistration.tsx, ModalRegistrationManual.tsx) - "Add Ambulance"
// raises the victim count, so it shouldn't be able to push it past what an
// operator could ever pick directly.
const MAX_VICTIMS = 5;
// Matches api-dsh's DISPATCH_REQUEST_TTL_SECONDS - the server is what
// actually decides a request is stale (against its own now()), this is only
// when the client re-checks. See expire_stale_dispatch_requests.py.
const DISPATCH_REQUEST_TTL_MS = 60_000;

interface DispatchConflictDetail {
  ambulance_id: number;
  reason: string;
  status?: string;
}

// AMB_RPT_ST_REG: the crew hasn't answered the request yet - distinguishable
// from a vehicle already mid-mission (post_report_dispatch.py's _busy_message
// carries the same distinction server-side for the fallback/log text).
const REG_STATUS = 'AMB_RPT_ST_REG';

// post_report_dispatch.py refuses a vehicle with no crew handset registered for
// push (app_push_token). Distinct from the vehicle-state conflicts above: the
// ambulance is available, but nobody could be told it was dispatched.
const NO_REGISTERED_DEVICE_REASON = 'ambulance_no_registered_device';

// Takes `t` rather than calling useTranslation: it is a plain helper, and the
// singular/plural split is two tags because the dictionary has no plural forms.
const formatVictims = (count: number, t: TFunction) =>
  `${count} ${count === 1 ? t('cmn>master>person') : t('cmn>master>persons')}`;

const AmbulanceDispatch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportIdParam = searchParams.get('reportId');
  const parsedReportId = reportIdParam ? Number(reportIdParam) : NaN;
  const reportId = Number.isFinite(parsedReportId) ? parsedReportId : undefined;

  const [activeTab, setActiveTab] = useState('status');

  const tabsFilter = [
    { id: 'status', label: t('dispatch>master>status') },
    { id: 'owner', label: t('dispatch>master>ownership') },
  ];

  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setKeyword(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    ambulances,
    reportHeader,
    isLoading,
    isError,
    refetch,
    dispatch,
    isDispatching,
    isRefreshing,
  } = useAmbulanceDispatch(reportId, {
    sortBy: activeTab === 'status' ? 'status' : 'owner',
    keyword,
  });

  const [ambulanceCount, setAmbulanceCount] = useState(1);
  const [modalAddAmbulance, setModalAddAmbulance] = useState(false);

  const createSlots = (count: number) =>
    Array.from({ length: count }, (_, index) => ({
      id: index + 1,
      ambulanceId: null as number | null,
    }));

  const [slots, setSlots] = useState(() => createSlots(ambulanceCount));

  // const [slots, setSlots] = useState([
  //   {
  //     id: 1,
  //     ambulanceId: null as number | null,
  //   },
  //   {
  //     id: 2,
  //     ambulanceId: null as number | null,
  //   },
  // ]);

  // The candidate list is paginated and sorted (by status or distance) - the
  // instant a slot's ambulance is dispatched, its status flips STBY -> REQ,
  // which ranks after every other status in cm_code.sort_order. With enough
  // offline/standby vehicles ahead of it, it drops off the current page
  // entirely, so re-deriving the slot's card from `ambulances.find(...)`
  // makes it vanish the moment the post-dispatch refetch lands - nothing to
  // do with rejection or expiry. Pin each picked ambulance's details here at
  // selection time so the slot's card never depends on the candidate list
  // still containing it later.
  const [pinnedAmbulances, setPinnedAmbulances] = useState<
    Map<number, (typeof ambulances)[number]>
  >(new Map());

  //   const selectedAmbulances = ambulanceData.filter((item) => selectedIds.includes(item.id));
  const handleSelect = (id: number) => {
    const existSlot = slots.find((slot) => slot.ambulanceId === id);

    if (existSlot) {
      setSlots((prev) =>
        prev.map((slot) => (slot.ambulanceId === id ? { ...slot, ambulanceId: null } : slot))
      );

      return;
    }

    const emptySlot = slots.find((slot) => slot.ambulanceId === null);

    if (!emptySlot) {
      showToast({
        type: 'error',
        title: 'Report Rejected',
        message: 'Pleaase add ambulance first',
      });
      // alert('Tambah slot ambulance terlebih dahulu');
      return;
    }

    const picked = ambulances.find((item) => item.id === id);
    if (picked) {
      setPinnedAmbulances((prev) => new Map(prev).set(id, picked));
    }
    setSlots((prev) =>
      prev.map((slot) => (slot.id === emptySlot.id ? { ...slot, ambulanceId: id } : slot))
    );
  };

  // Request Dispatch is per-slot now (see PlaceHolderAmbulance) - each click
  // dispatches just that one ambulance, not the whole selection at once.
  // dispatchedIds tracks which ambulances have a live (still-pending)
  // request this session, so a slot's button can't be double-submitted;
  // requestingId tracks which one is in flight so only that slot's button
  // shows loading. expiryTimers holds the pending 60s re-check per ambulance.
  const [dispatchedIds, setDispatchedIds] = useState<Set<number>>(new Set());
  const [requestingId, setRequestingId] = useState<number | null>(null);
  const expiryTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(
    () => () => {
      for (const timer of expiryTimers.current.values()) clearTimeout(timer);
    },
    []
  );

  // Pre-fill slots once both the victim count and the fleet list are known -
  // only once, so it never overwrites ambulances the operator already picked.
  // A report already 3-of-5 accepted (e.g. reached via Re-Dispatch
  // Replacement after 2 rejections) must show only the 2 actually-empty
  // slots, not 5 - the 3 already-active ambulances get pre-filled in and
  // locked (isRequested), leaving just the real gap for the operator to fill.
  //
  // Also waits out isRefreshing, not just isLoading: these queries have no
  // staleTime, so React Query shows cached data immediately on mount (isLoading
  // already false) while it quietly refetches in the background. Without this,
  // navigating back to a report shortly after it changed elsewhere (crew app,
  // another tab) could pre-fill from that stale snapshot instead of waiting
  // the beat for the real one.
  const hasInitializedSlots = useRef(false);
  useEffect(() => {
    if (hasInitializedSlots.current || !reportHeader || isLoading || isRefreshing) return;
    hasInitializedSlots.current = true;

    const active = ambulances.filter((item) => item.isActiveAssignment);
    const count = Math.max(1, reportHeader.victimsCount, active.length);
    setAmbulanceCount(count);
    setSlots(
      Array.from({ length: count }, (_, index) => ({
        id: index + 1,
        ambulanceId: active[index]?.id ?? null,
      }))
    );
    if (active.length > 0) {
      setPinnedAmbulances((prev) => {
        const next = new Map(prev);
        for (const item of active) next.set(item.id, item);
        return next;
      });
      setDispatchedIds((prev) => new Set([...prev, ...active.map((item) => item.id)]));
    }
  }, [reportHeader, ambulances, isLoading, isRefreshing]);

  // A dispatched-but-still-pending ambulance whose fresh `disabled` flag
  // comes back true was rejected - either the crew declined, or (far more
  // often) nobody answered within DISPATCH_REQUEST_TTL_SECONDS and the
  // server's sweep expired it (see get_report_ambulance_list.py). Either
  // way the operator needs to pick a different ambulance for that slot.
  useEffect(() => {
    if (dispatchedIds.size === 0) return;
    for (const id of dispatchedIds) {
      const fresh = ambulances.find((item) => item.id === id);
      if (!fresh?.disabled) continue;

      const timer = expiryTimers.current.get(id);
      if (timer) {
        clearTimeout(timer);
        expiryTimers.current.delete(id);
      }
      setDispatchedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setSlots((prev) =>
        prev.map((slot) => (slot.ambulanceId === id ? { ...slot, ambulanceId: null } : slot))
      );
      setPinnedAmbulances((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      showToast({
        type: 'warning',
        title: t('dispatch>master>ambulance did not respond'),
        message: t('dispatch>master>ambulance did not respond detail'),
      });
    }
  }, [ambulances, dispatchedIds, t]);

  // Every required slot has a live (non-rejected) request in flight - just a
  // confirmation toast. Stays on this screen either way: the operator closes
  // manually (the Close button below) rather than being navigated anywhere
  // automatically.
  const hasShownCompletionToast = useRef(false);
  useEffect(() => {
    if (hasShownCompletionToast.current) return;
    if (slots.length < ambulanceCount) return;
    const allRequested = slots.every(
      (slot) => slot.ambulanceId !== null && dispatchedIds.has(slot.ambulanceId)
    );
    if (!allRequested) return;
    hasShownCompletionToast.current = true;
    showToast({
      type: 'success',
      title: t('dispatch>master>all ambulances requested'),
      message: t('dispatch>master>all ambulances requested detail'),
    });
  }, [slots, dispatchedIds, ambulanceCount, t]);

  const handleRequestDispatch = (ambulanceId: number) => {
    if (reportId == null) return;
    // Pinned first - by the time of this click the candidate list may
    // already have re-sorted this ambulance off its current page.
    const ambulance =
      pinnedAmbulances.get(ambulanceId) ?? ambulances.find((item) => item.id === ambulanceId);
    if (!ambulance) return;

    // Which slot THIS ambulance actually occupies, not just whether an
    // additional slot exists anywhere - an extra slot sitting empty must not
    // bump number_of_victims just because some unrelated original slot (e.g.
    // a redispatch after a rejection) happens to dispatch first.
    const slotIndex = slots.findIndex((slot) => slot.ambulanceId === ambulanceId);
    const isAdditionalSlot = slotIndex >= ambulanceCount;

    setRequestingId(ambulanceId);
    dispatch(
      {
        ambulanceIds: [ambulanceId],
        // Only bumps number_of_victims when this specific dispatch fills a
        // slot added beyond the report's original victim count - every other
        // dispatch (including a redispatch into an original slot, even while
        // an additional slot exists unfilled) leaves this undefined and the
        // request patches nothing, exactly like before "Add Ambulance" existed.
        numberOfVictims: isAdditionalSlot ? slots.length : undefined,
      },
      {
        onSuccess: () => {
          setRequestingId(null);
          setDispatchedIds((prev) => new Set(prev).add(ambulanceId));
          expiryTimers.current.set(
            ambulanceId,
            setTimeout(() => refetch(), DISPATCH_REQUEST_TTL_MS)
          );
          showToast({
            type: 'info',
            title: t('psc>msg>dispatch requested'),
            message: t('psc>msg>dispatch requested detail'),
          });

          // The crew is told by the push notification the backend sends in the
          // same request (push_ambulance_dispatch), and the dispatch is refused
          // outright if no handset is registered - so there is nothing for the
          // operator to forward by hand.
          //
          // Stays on this screen if other slots still need requesting - the
          // "all requested" effect above navigates away once every slot has a
          // live request. useAmbulanceDispatch's alreadyDispatched redirect is
          // separate: it only fires once a crew accepts, not on a bare
          // request, so this page won't get yanked out mid-multi-victim-dispatch.
        },
        onError: (error: unknown) => {
          setRequestingId(null);
          const conflict = (
            error as {
              response?: {
                data?: { data?: { reason?: string; details?: DispatchConflictDetail[] } };
              };
            }
          )?.response?.data?.data;
          const details = conflict?.details;
          const allNotAccepted =
            !!details && details.length > 0 && details.every((d) => d.status === REG_STATUS);
          // The crew app was never signed in on this vehicle, so there is no
          // handset to push the dispatch to. Nothing about the ambulance itself
          // is wrong - the operator just has to pick one whose crew is on the
          // app. See post_report_dispatch.py's readiness gate.
          const noRegisteredDevice = conflict?.reason === NO_REGISTERED_DEVICE_REASON;
          showToast({
            type: 'error',
            title: t('psc>msg>dispatch failed'),
            message: noRegisteredDevice
              ? t('psc>msg>ambulance not logged in')
              : allNotAccepted
                ? t('psc>msg>ambulance not accepted yet')
                : ((error as Error)?.message ?? t('psc>msg>failed request dispatch')),
          });
        },
      }
    );
  };

  if (!reportId) {
    return (
      <section className="_ambulance bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <span className="title1bold text-text-grey">
            Missing report ID - open this page with ?reportId=&lt;id&gt; from a report.
          </span>
        </div>
      </section>
    );
  }

  // Out-of-scope report id (403) or any other load failure - an explicit
  // state instead of an indefinite spinner, since the query never resolves
  // to data for either the list or header call.
  if (isError && !isLoading) {
    return (
      <section className="_ambulance bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
        <Header />
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <span className="title1bold text-text-grey">
            {t('psc>msg>report not accessible', 'This report is not accessible.')}
          </span>
          <Button
            variant="default"
            onClick={() => navigate('/dashboard/psc-dashboard')}
            label={t('cmn>master>close')}
            customButton=""
            className="btn-outline-blue uppercase text-[20px]"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="_ambulance-wrap bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <Header />
      <div className=" _ambulance-screen p-3 w-full h-[calc(95vh-12px)] grid grid-cols-[2.5fr_1fr] gap-3 overflow-hidden">
        <section className="w-full flex  flex-col h-full overflow-hidden rounded-[12px] border bg-db-card bg-db-border">
          <div className="p-6 flex flex-col gap-6 w-full h-full overflow-auto">
            {/* <BackLink label="back" onclick={() => alert('klik aku mas')} /> */}
            <div className="flex flex-col gap-1">
              <span className="title1bold">{t('dispatch>master>ambulance list')} </span>
              <span className="text-text-grey capitalize dark:text-white title2medium">
                {t('dispatch>master>ranked')}
              </span>
            </div>

            <div className="gap-3 grid grid-cols-[1.8fr_1fr] w-full justify-between flex-row items-center">
              <div className=" flex items-center inline-flex gap-10 ">
                <input
                  type="text"
                  className="input-db-text rounded-[50px]! w-full  "
                  placeholder={t('dispatch>master>search a healthcare or ownership')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <div className="inline-flex gap-1 items-center w-full">
                  <span className=" min-w-[90px] uppercase text-text-grey title2">
                    {t('dispatch>master>sort by')}:
                  </span>
                  <TabControl tabs={tabsFilter} activeTab={activeTab} onChange={setActiveTab} />
                </div>
              </div>
              <div className="w-fit ml-auto">
                <button
                  className="w-fit py-2 px-3 border inline-flex gap-2 bg-white items-center border-text-blue-100 rounded-full disabled:opacity-60"
                  type="button"
                  onClick={() =>
                    refetch().then(() =>
                      showToast({
                        type: 'success',
                        title: t('psc>msg>refreshed'),
                        message: t('psc>msg>refreshed detail'),
                      })
                    )
                  }
                  disabled={isRefreshing}
                >
                  <RotateCw color="#007aff" className={cn(isRefreshing && 'animate-spin')} />
                  <span className="title2 text-text-blue-100 capitalize">refresh</span>
                </button>
              </div>
            </div>

            <div className="space-y-3 w-full">
              {ambulances.map((item, index) => (
                <AmbulanceList
                  key={item.id}
                  {...item}
                  no={index + 1}
                  selected={slots.some((slot) => slot.ambulanceId === item.id)}
                  onClick={() => handleSelect(item.id)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full flex! gap-3  flex-col h-full overflow-auto rounded-[12px]">
          <div className="w-full flex h-full flex-col gap-6 overflow-auto">
            <div className="w-full flex  min-h-[300px] flex-col gap-6 p-6 overflow-auto rounded-[12px] border bg-db-card bg-db-border">
              <div className="flex flex-col gap-1">
                <span className="text-text-grey uppercase dark:text-white/70 title2medium">
                  {t('cmn>master>report id')}
                </span>
                <span className="title1bold">{reportHeader?.reportCode ?? 'Loading…'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-text-grey uppercase dark:text-white/70 title2medium">
                  {t('psc>report>incident location')}
                </span>
                <span className="title1">{reportHeader?.incidentLocation ?? 'Loading…'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-text-grey uppercase dark:text-white/70 title2medium">
                  {t('psc>report>number of victims')}
                </span>
                <span className="title1">
                  {reportHeader ? formatVictims(reportHeader.victimsCount, t) : 'Loading…'}
                </span>
              </div>
            </div>
            <div className="w-full flex min-h-[400px] flex-1 flex-col gap-2 p-6 overflow-auto rounded-[12px] border bg-db-card bg-db-border">
              <div className="inline-flex justify-between gap-3 items-center">
                <span className="title2 uppercase text-text-grey">dispatch team</span>
                <Button
                  onClick={() => setModalAddAmbulance(true)}
                  variant="default"
                  disabled={slots.length >= MAX_VICTIMS}
                  className="w-fit rounded-full btn-fill-blue capitalize disabled:opacity-50"
                >
                  <Plus />
                  <span className="title1">{t('dispatch>master>add ambulance')}</span>
                </Button>
              </div>

              <div className="flex w-full flex-col gap-3">
                {slots.map((slot, index) => {
                  // Pinned first - the paginated/sorted candidate list can
                  // drop this ambulance off its current page the instant its
                  // status changes (e.g. dispatched -> AMB_ST_REQ), which
                  // must not make an already-picked slot look empty again.
                  const ambulance = slot.ambulanceId
                    ? (pinnedAmbulances.get(slot.ambulanceId) ??
                      ambulances.find((item) => item.id === slot.ambulanceId))
                    : undefined;

                  const isAdditional = index >= ambulanceCount;

                  return (
                    <PlaceHolderAmbulance
                      key={slot.id}
                      ambulanceName={`${t('cm > ambulance')} ${index + 1}`}
                      selected={!!ambulance}
                      plateNumber={ambulance?.plateNumber}
                      hospital={ambulance?.hospital}
                      showRemove={isAdditional}
                      onRemove={() => {
                        if (!ambulance) return;

                        setSlots((prev) =>
                          prev.map((item) =>
                            item.id === slot.id ? { ...item, ambulanceId: null } : item
                          )
                        );
                        setPinnedAmbulances((prev) => {
                          const next = new Map(prev);
                          next.delete(ambulance.id);
                          return next;
                        });

                        handleSelect(ambulance.id);
                      }}
                      onRemovePlaceolder={() => {
                        setSlots((prev) => prev.filter((item) => item.id !== slot.id));
                      }}
                      onRequest={() => {
                        // dispatch is one shared mutation - guard against a
                        // second slot firing while another is still in flight.
                        if (ambulance && !isDispatching) handleRequestDispatch(ambulance.id);
                      }}
                      isRequesting={!!ambulance && requestingId === ambulance.id}
                      isRequested={!!ambulance && dispatchedIds.has(ambulance.id)}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div className="w-full flex h-fit flex-col gap-2">
            <Button
              variant="default"
              onClick={() => navigate('/dashboard/psc-dashboard')}
              label={t('cmn > close')}
              customButton=""
              className="flex-1 btn-outline-blue   uppercase  text-[20px] "
            />
          </div>
        </section>
      </div>
      <ModalAddAmbulance
        isOpen={modalAddAmbulance}
        onClose={() => setModalAddAmbulance(false)}
        onConfirm={() => {
          setSlots((prev) => [
            ...prev,
            {
              id: Math.max(...prev.map((x) => x.id), 0) + 1,
              ambulanceId: null,
            },
          ]);
          setModalAddAmbulance(false);
        }}
      />
    </section>
  );
};

export default AmbulanceDispatch;
